<script setup lang="ts">
import { onMounted, onUnmounted, ref, useId } from 'vue'

import StIconButton from './StIconButton.vue'

const props = withDefaults(defineProps<{ title: string, wide?: boolean, appearance?: 'standard' | 'legacy', initialFocus?: () => HTMLElement | null, returnFocus?: () => HTMLElement | null, restoreFocus?: boolean, dismissible?: boolean }>(), { restoreFocus: true, dismissible: true })
const emit = defineEmits<{ close: [] }>()
const dialog = ref<HTMLDialogElement>()
const headingId = useId()
const backdropPress = ref(false)
const previous = document.activeElement as HTMLElement | null
function closeFromBackdrop(event: MouseEvent) {
  // A drag that starts on a control must not become a backdrop dismissal.
  if (props.dismissible && backdropPress.value && event.target === dialog.value)
    emit('close')
  backdropPress.value = false
}
function trapFocus(event: KeyboardEvent) {
  if (event.key !== 'Tab' || !dialog.value)
    return
  const controls = [...dialog.value.querySelectorAll<HTMLElement>('button:not(:disabled),input:not(:disabled),select:not(:disabled),textarea:not(:disabled),a[href],[tabindex]:not([tabindex="-1"])')].filter(el => el.getClientRects().length && getComputedStyle(el).visibility !== 'hidden')
  const first = controls[0]
  const last = controls.at(-1)
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault()
    last?.focus()
  }
  else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault()
    first?.focus()
  }
}
onMounted(() => {
  dialog.value?.showModal()
  props.initialFocus?.()?.focus({ preventScroll: true })
})
onUnmounted(() => {
  if (props.restoreFocus) {
    const target = previous?.isConnected && previous !== document.body ? previous : props.returnFocus?.()
    target?.focus({ preventScroll: true })
  }
})
</script>

<template>
  <dialog ref="dialog" class="modal st-dialog" :class="{ wide, 'st-dialog-surface': appearance !== 'legacy' }" :aria-labelledby="headingId" @keydown="trapFocus" @cancel.prevent="dismissible && emit('close')" @pointerdown="backdropPress = $event.target === dialog" @pointerup="backdropPress = backdropPress && $event.target === dialog" @click="closeFromBackdrop">
    <header>
      <h2 :id="headingId" :class="{ 'st-visually-hidden': $slots.header }">
        {{ title }}
      </h2><slot name="header">
        <StIconButton label="Close dialog" :disabled="!dismissible" @click="emit('close')">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.65" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18" /></svg>
        </StIconButton>
      </slot>
    </header><div class="modal-content st-dialog-content">
      <slot />
    </div>
  </dialog>
</template>
